<script setup lang="ts">
  import { useDebounceFn } from '@vueuse/core'

  const { locale } = useI18n()

  withDefaults(
    defineProps<{
      /** Vacío (`''`) si usas etiqueta flotante fuera del componente. */
      placeholder?: string
      disabled?: boolean
    }>(),
    { placeholder: 'Madrid', disabled: false },
  )

  const selectedLocation = defineModel<CityCountrySelection>()
  const runtimeConfig = useRuntimeConfig()

  /** Equivale a legacy `types: ['(cities)']`. */
  const CITY_PRIMARY_TYPES = ['locality', 'administrative_area_level_3'] as const

  type PlacePredictionForPick = {
    placeId?: string
    text: { text: string }
    mainText?: { text: string }
    secondaryText?: { text: string }
    toPlace: () => unknown
  }

  type CityMenuItem = {
    id: string
    type: 'item'
    name_translated: string
    label: string
    destination_name?: string
    country_name_translated?: string
    icon: string
    /** Solo ausente en valores “solo texto” sincronizados desde el padre; las sugerencias de Google lo llevan siempre. */
    prediction?: PlacePredictionForPick
  }

  function minimalItemFromCity(city: string, country: string): CityMenuItem {
    const id = `${country}-${city}`
    return {
      id,
      type: 'item',
      name_translated: city,
      label: city,
      icon: 'i-heroicons-map-pin-solid',
    }
  }

  const menuModel = ref<CityMenuItem | null>(
    selectedLocation.value?.city
      ? minimalItemFromCity(
          selectedLocation.value.city,
          selectedLocation.value.country ?? '',
        )
      : null,
  )

  /** `UInputMenu` no admite `null` en el modelo. */
  const menuModelForInput = computed({
    get: (): CityMenuItem | undefined => menuModel.value ?? undefined,
    set: (v: CityMenuItem | undefined) => {
      menuModel.value = v ?? null
    },
  })
  const searchTerm = ref('')
  const menuOpen = ref(false)
  const suggestionItems = ref<CityMenuItem[]>([])
  const fetching = ref(false)

  let sessionToken: object | null = null

  type PlacesLib = {
    AutocompleteSuggestion: {
      fetchAutocompleteSuggestions: (req: Record<string, unknown>) => Promise<{ suggestions: Array<{ placePrediction?: PlacePredictionForPick }> }>
    }
    AutocompleteSessionToken: new () => object
  }

  const { load } = useScript({
    src: `https://maps.googleapis.com/maps/api/js?key=${runtimeConfig.public.GOOGLE_API_KEY}&libraries=places&loading=async`,
    async: true,
    defer: true,
  })

  type AddressComponentLike = { types: string[]; longText?: string; shortText?: string }

  function displayNameText(place: { displayName?: string | { text: string } }): string {
    const d = place.displayName
    if (!d) return ''
    return typeof d === 'string' ? d : d.text
  }

  function extractCityCountry(place: {
    addressComponents?: AddressComponentLike[]
    displayName?: string | { text: string }
  }): CityCountrySelection {
    const components = place.addressComponents
    let city = ''
    let country = ''

    if (components?.length) {
      const locality = components.find(c => c.types.includes('locality'))
      const countryComp = components.find(c => c.types.includes('country'))
      city = locality?.longText ?? locality?.shortText ?? ''

      const shortCountry = countryComp?.shortText
      const longCountry = countryComp?.longText
      country = (shortCountry !== undefined && shortCountry !== '') ? shortCountry : (longCountry ?? '')
    }

    if (!city) {
      city = displayNameText(place)
    }

    return { city: city || '', country: country || '' }
  }

  async function getPlacesLib(): Promise<PlacesLib | null> {
    if (typeof window === 'undefined') return null
    const maps = (window as unknown as { google?: { maps?: { importLibrary?: (lib: string) => Promise<PlacesLib> } } }).google?.maps
    if (!maps?.importLibrary) return null
    return maps.importLibrary('places')
  }

  function resetSession(lib: PlacesLib) {
    sessionToken = new lib.AutocompleteSessionToken()
  }

  /** Texto de país en lista: no afecta al valor seleccionado (solo ciudad en el input / v-model). */
  function predictionRegionCountryLine(p: PlacePredictionForPick, cityLine: string): string {
    const secondary = p.secondaryText?.text?.trim() ?? ''
    if (secondary) {
      return secondary
    }
    const full = p.text?.text?.trim() ?? ''
    if (full.length > cityLine.length) {
      return full.slice(cityLine.length).replace(/^[,\s]+/, '').trim()
    }
    return ''
  }

  function toMenuItem(p: PlacePredictionForPick, index: number): CityMenuItem {
    const main = p.mainText?.text ?? p.text.text
    const regionCountry = predictionRegionCountryLine(p, main)
    return {
      id: p.placeId ?? `${main}-${index}`,
      type: 'item',
      name_translated: main,
      label: main,
      country_name_translated: regionCountry,
      icon: 'i-heroicons-map-pin-solid',
      prediction: p,
    }
  }

  const debouncedFetch = useDebounceFn(async (q: string) => {
    const trimmed = q.trim()
    if (trimmed.length < 2) {
      suggestionItems.value = []
      fetching.value = false
      return
    }

    const lib = await getPlacesLib()
    if (!lib) {
      fetching.value = false
      return
    }

    if (!sessionToken) {
      resetSession(lib)
    }

    fetching.value = true
    try {
      const { suggestions: rows } = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: trimmed,
        sessionToken,
        includedPrimaryTypes: [...CITY_PRIMARY_TYPES],
        language: locale.value,
      })

      const next: CityMenuItem[] = []
      let i = 0
      for (const row of rows ?? []) {
        const p = row.placePrediction
        if (!p?.text?.text || !p.toPlace) continue
        next.push(toMenuItem(p, i))
        i++
      }
      suggestionItems.value = next
    }
    catch {
      suggestionItems.value = []
    }
    finally {
      fetching.value = false
    }
  }, 300)

  const menuItems = computed(() => suggestionItems.value)

  watch(searchTerm, (v) => {
    if (!menuOpen.value) return
    void debouncedFetch(v ?? '')
  })

  watch(menuOpen, async (open) => {
    if (!open) return
    const lib = await getPlacesLib()
    if (lib && !sessionToken) {
      resetSession(lib)
    }
    const q = searchTerm.value?.trim() ?? ''
    if (q.length >= 2) {
      void debouncedFetch(q)
    }
  })

  async function pickPrediction(pred: PlacePredictionForPick) {
    const place = pred.toPlace() as {
      fetchFields: (o: { fields: string[] }) => Promise<void>
      addressComponents?: AddressComponentLike[]
      displayName?: string | { text: string }
    }
    await place.fetchFields({ fields: ['addressComponents', 'displayName'] })
    const { city, country } = extractCityCountry(place)
    selectedLocation.value = { city, country }
    menuModel.value = {
      ...toMenuItem(pred, 0),
      id: `${country}-${city}`,
      name_translated: city,
      label: city,
    }
    suggestionItems.value = []

    const lib = await getPlacesLib()
    if (lib) {
      resetSession(lib)
    }
  }

  async function onMenuUpdate(val: CityMenuItem | undefined) {
    if (val == null) {
      selectedLocation.value = { city: '', country: '' }
      return
    }
    if (val.prediction) {
      await pickPrediction(val.prediction)
    }
  }

  watch(
    () => [selectedLocation.value?.city, selectedLocation.value?.country] as const,
    ([city, country]) => {
      if (city) {
        const cur = menuModel.value
        if (!cur || cur.name_translated !== city) {
          menuModel.value = minimalItemFromCity(city, country ?? '')
        }
      }
      else {
        menuModel.value = null
      }
    },
    { deep: true },
  )

  watch(locale, () => {
    if (!menuOpen.value) return
    const q = searchTerm.value?.trim() ?? ''
    if (q.length >= 2) {
      void debouncedFetch(q)
    }
  })

  onMounted(async () => {
    await load()
  })

  const placesMenuUi = {
    root: 'w-full max-w-full min-w-0',
    content:
      'max-h-fit min-w-0 flex flex-col overflow-x-clip overflow-y-auto w-[min(calc(100vw-2rem),var(--reka-combobox-trigger-width))] sm:min-w-64',
  }
</script>

<template>
  <UInputMenu
    v-model="menuModelForInput"
    label-key="name_translated"
    v-model:search-term="searchTerm"
    v-model:open="menuOpen"
    :items="menuItems"
    :loading="fetching"
    :placeholder="placeholder"
    :disabled="disabled"
    class="truncate w-full"
    ignore-filter
    size="lg"
    trailing-icon=""
    @update:model-value="onMenuUpdate"
    :ui="placesMenuUi"
    :content="{
      align: 'start',
    }"
  >
    <template #item-label="{ item }">
      <span class="block w-full min-w-0 max-w-full truncate text-start">
        {{ item.label }} <template v-if="item.country_name_translated"><span class="text-dimmed"> {{ item.country_name_translated }}</span></template>
      </span>
    </template>

    <template #content-bottom>
      <div class="flex min-w-0 max-w-full justify-end border-default border-t px-2 py-1.5 items-center gap-2 overflow-hidden">
        <span class="text-dimmed text-[10px]">Powered by </span>
        <img
          src="https://storage.googleapis.com/geo-devrel-public-buckets/powered_by_google_on_white.png"
          alt="Google"
          width="120"
          height="16"
          loading="lazy"
          class="h-3.5 w-auto opacity-80"
        >
      </div>
    </template>
  </UInputMenu>
</template>
